import React, { useState } from "react";
import { api, unwrap, errMsg } from "@/lib/api";
import { useMutation } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Search, UserCheck, AlertTriangle } from "lucide-react";

export default function Kiosk() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [confirmed, setConfirmed] = useState(null);
  const search = useMutation({
    mutationFn: async () => unwrap(await api.get(`/kiosk/lookup?q=${encodeURIComponent(q)}`)),
    onSuccess: (d) => setResults(d),
    onError: (e) => toast.error(errMsg(e)),
  });
  const check = useMutation({
    mutationFn: async ({ student_id, pickup_person_id }) => unwrap(await api.post("/kiosk/checkout", { student_id, pickup_person_id, matched_by: "photo" })),
    onSuccess: (d) => { setConfirmed(d); toast.success(`${d.student_name} checked out safely`); setTimeout(() => { setConfirmed(null); setResults([]); setQ(""); }, 4000); },
    onError: (e) => toast.error(errMsg(e)),
  });
  return (
    <div className="min-h-screen bg-[var(--surface-1)] p-6 lg:p-10" data-testid="kiosk-page" dir="auto">
      <div className="max-w-4xl mx-auto">
        <h1 className="font-display text-4xl font-bold text-[var(--text-primary)] mb-2">Reception check-out</h1>
        <p className="text-sm text-[var(--text-muted)] mb-6">Find the child, confirm the authorised person, tap to release.</p>
        <div className="flex gap-2 mb-6">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Admission no. or name…" onKeyDown={(e) => { if (e.key === "Enter" && q) search.mutate(); }} className="text-lg h-14" data-testid="kiosk-search" />
          <Button size="lg" onClick={() => search.mutate()} disabled={!q || search.isPending} className="h-14 px-6 bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white" data-testid="kiosk-search-btn"><Search className="h-5 w-5 mr-2" />Find</Button>
        </div>
        {confirmed && (
          <Card className="border-[var(--success)] border-2 mb-4"><CardContent className="p-6 flex items-center gap-4"><UserCheck className="h-10 w-10 text-[var(--success)]" /><div><div className="font-display text-2xl font-bold text-[var(--success)]">Released to {confirmed.pickup_person.name}</div><div className="text-sm text-[var(--text-muted)]">{confirmed.student_name} · {confirmed.verified_at.slice(11, 19)}</div></div></CardContent></Card>
        )}
        {results.map((s) => (
          <Card key={s.id} className="border-[var(--subtle)] mb-4" data-testid={`kiosk-child-${s.id}`}>
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                {s.photo_url ? (
                  <img src={s.photo_url} alt={s.first_name} className="w-24 h-24 rounded-full object-cover border-2 border-[var(--subtle)]" />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-[var(--accent-bg)] flex items-center justify-center text-3xl font-bold text-[var(--accent)]">{(s.first_name || "?").charAt(0)}</div>
                )}
                <div className="flex-1">
                  <div className="font-display text-2xl font-bold">{s.first_name} {s.last_name}</div>
                  <div className="text-sm text-[var(--text-muted)] font-mono-num">{s.admission_no}</div>
                  {(s.allergies || []).length > 0 && (
                    <div className="mt-2 flex items-center gap-2 text-[var(--error)] text-sm font-semibold"><AlertTriangle className="h-4 w-4" /> Allergies: {s.allergies.join(", ")}</div>
                  )}
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-[var(--subtle)]">
                <div className="text-xs uppercase tracking-wider text-[var(--text-muted)] mb-2">Authorised pickup persons</div>
                {(s.authorised_pickups || []).length === 0 ? (
                  <div className="text-sm text-[var(--error)] font-semibold">No authorised persons registered — contact admin.</div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {(s.authorised_pickups || []).map((p) => (
                      <button key={p.id} onClick={() => check.mutate({ student_id: s.id, pickup_person_id: p.id })} disabled={check.isPending}
                        className="text-left border border-[var(--subtle)] rounded-lg p-3 hover:bg-[var(--accent-bg)] hover:border-[var(--accent)] transition-colors" data-testid={`kiosk-verify-${p.id}`}>
                        <div className="flex items-center gap-3">
                          {p.photo_url ? <img src={p.photo_url} alt={p.name} className="w-12 h-12 rounded-full object-cover" /> : <div className="w-12 h-12 rounded-full bg-[var(--subtle-bg)] flex items-center justify-center text-lg font-bold">{p.name.charAt(0)}</div>}
                          <div>
                            <div className="font-semibold text-sm">{p.name}</div>
                            <div className="text-xs text-[var(--text-muted)]">{p.relation} · {p.phone}</div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
        {results.length === 0 && !search.isPending && q && !confirmed && (
          <div className="text-center py-12 text-[var(--text-muted)]">No child found. Try a different admission number or name.</div>
        )}
      </div>
    </div>
  );
}
